import { Component } from '@theme/component';

/**
 * @typedef {Object} CollectionDescriptionRefs
 * @property {HTMLElement} content - The description content element
 * @property {HTMLElement} toggle - The read more / read less button
 * @property {HTMLElement} labelMore - The read more label
 * @property {HTMLElement} labelLess - The read less label
 */

/**
 * Expands and collapses a line-clamped collection description.
 *
 * @extends {Component<CollectionDescriptionRefs>}
 */
class CollectionDescriptionReadMore extends Component {
  requiredRefs = ['content', 'toggle', 'labelMore', 'labelLess'];

  /** @type {boolean} */
  #expanded = false;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#updateOverflow();

    if ('ResizeObserver' in window) {
      this.#resizeObserver = new ResizeObserver(() => {
        if (!this.#expanded) this.#updateOverflow();
      });
      this.#resizeObserver.observe(this.refs.content);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  /**
   * Shows or hides the toggle button.
   * @param {boolean} visible
   */
  #setToggleVisible(visible) {
    this.refs.toggle.hidden = !visible;
  }

  /**
   * Updates whether content overflows the clamped height.
   */
  #updateOverflow() {
    const { content } = this.refs;
    const isOverflowing = content.scrollHeight > content.clientHeight + 1;
    this.#setToggleVisible(isOverflowing);
  }

  /**
   * Syncs expanded UI state on the host and labels.
   */
  #syncExpandedState() {
    const { toggle, labelMore, labelLess } = this.refs;

    this.dataset.expanded = String(this.#expanded);
    toggle.setAttribute('aria-expanded', String(this.#expanded));
    labelMore.hidden = this.#expanded;
    labelLess.hidden = !this.#expanded;
  }

  /**
   * Toggles between clamped and full description.
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();

    this.#expanded = !this.#expanded;
    this.#syncExpandedState();

    if (!this.#expanded) {
      requestAnimationFrame(() => this.#updateOverflow());
    }
  };
}

if (!customElements.get('collection-description-read-more')) {
  customElements.define('collection-description-read-more', CollectionDescriptionReadMore);
}
